// Видимость курса — состояние модерации бэка (`ModerationState.visibility`); любое её
// изменение идёт через `PATCH /visibility`: бэк сам решает, применить сразу или
// отправить курс на LLM-проверку.
import { useEffect, useRef, useState } from 'react'
import type { CourseWithoutBlocks } from '@/types/course'
import type { ValidationError } from '@/types/api'
import type { AccessType } from '@/features/instructions/types'
import { useSetCourseVisibilityMutation } from '@/features/moderation/moderationApi'
import { useCourseModeration } from '@/features/moderation/useCourseModeration'
import { fromVisibility, toVisibility } from '@/features/moderation/lib/visibility'
import { applyVisibility, type VisibilityTexts } from '@/features/moderation/lib/visibilityChange'
import { useRetryCountdown } from './useRetryCountdown'
import { messagesNow } from '@/i18n'
import { messages } from './messages'

// Функция, а не константа: тосты — на языке интерфейса в момент смены доступа.
const texts = (): VisibilityTexts => {
  const m = messagesNow(messages)
  return {
    review: m.sentToReview,
    done: { private: m.closed, link: m.byLink, public: m.inCatalog },
  }
}

const OPTIMISTIC_HOLD_MS = 1500

export const useCourseAccess = (course: CourseWithoutBlocks) => {
  const [setVisibility, { isLoading }] = useSetCourseVisibilityMutation()
  const { state, isError, refetch } = useCourseModeration(course.id)
  const countdown = useRetryCountdown()
  // Список проблем публикации показывает ValidationErrorsDialog: в один toast он не влезает —
  // невалидный курс отдаёт ошибку на каждый проблемный блок, это неразборчивая строка.
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([])

  // Выбор автора виден в селекте СРАЗУ, пока бэк отвечает: иначе селект стоял на
  // старом значении до ответа и перечитывания модерации. Правду возвращает ответ:
  // ушло на проверку или отказ — показ снова идёт от `visibility` сервера.
  // Снимается, когда после ответа приехало свежее состояние модерации (иначе селект
  // на миг откатывался бы к старому), а без него — через `OPTIMISTIC_HOLD_MS` (отказ).
  const [optimistic, setOptimistic] = useState<AccessType | null>(null)
  const settled = useRef(false)
  useEffect(() => {
    if (settled.current) {
      settled.current = false
      setOptimistic(null)
    }
  }, [state])

  const applyAccess = (next: AccessType): Promise<void> => {
    setValidationErrors([])
    settled.current = false
    setOptimistic(next)
    let accepted = false
    return applyVisibility({
      state,
      next,
      texts: texts(),
      request: () =>
        setVisibility({ id: course.id, visibility: toVisibility(next) })
          .unwrap()
          .then((res) => {
            accepted = true
            return res
          }),
      refetch,
      startCountdown: countdown.start,
      onValidation: setValidationErrors,
    }).finally(() => {
      // Отказ (429, ошибки публикации) — сразу назад к правде сервера.
      if (!accepted) return setOptimistic(null)
      settled.current = true
      window.setTimeout(() => setOptimistic(null), OPTIMISTIC_HOLD_MS)
    })
  }

  return {
    moderation: state,
    moderationError: isError,
    reloadModeration: refetch,
    // «Не знаем» ≠ «закрыт»: без состояния модерации доступ undefined, а не 'private'.
    access: state ? (optimistic ?? fromVisibility(state.visibility)) : undefined,
    applyAccess,
    isBusy: isLoading,
    retryLabel: countdown.label,
    validationErrors,
    clearValidationErrors: () => setValidationErrors([]),
  }
}
